<template lang="pug">
.settings
  .d-flex
    h5 Time tracking settings
    .line-container
      .line
  #track-time.form-group
    ValidationProvider(:model-value="ableToTrackTime")
      label.control-label
        .d-flex.align-items-center Tracking permissions
      .checkbox
        AppOnOffToggle(
          v-model="ableToTrackTime"
          :disabled="initialMembershipRole === 'project_viewer' || viewOnly"
        )
          span Able to track time
        i.hi.hi-info-filled.hi-12.a-tooltip.mx-5(
          v-if="initialMembershipRole === 'project_viewer' || viewOnly"
          v-tooltip
          :title="viewOnly ? 'Cannot enable time tracking for a view-only member' : 'Project viewers cannot track time'"
        )
  .d-flex.form-row.flex-column.mb-20
    #keep-idle-time.flex-grow-1
      ValidationProvider(:model-value="keepIdleTime")
        AppToggleButtonGroup(
          v-model="keepIdleTime"
          label="Keep idle time"
          label-tooltip="This setting applies to the desktop apps"
          disabled-tooltip="Upgrade to a Premium plan to change this setting"
          :items="KEEP_IDLE_TIME_ITEMS"
          :selected="keepIdleTime"
          :disabled="!hasKeepIdleTime"
        )
    #idle-timeout
      ValidationProvider(:model-value="idleTimeout")
        IdleTimeoutSelect(
          v-model="idleTimeout"
          label="Idle timeout"
          label-tooltip="This setting applies to the desktop apps"
          disabled-tooltip="Upgrade your plan to set a custom idle timeout between 1 - 60 minutes"
          :options="IDLE_TIMEOUT_ITEMS"
          :custom-disabled="customIdleTimeoutDisabled"
          :custom-is-selected="customIdleTimeoutSelected"
        )
  #modify-time-with-manual-approval.form-group(v-if="showModifyTimeWithManualTimeApproval")
    ValidationProvider(:model-value="ableToModifyTime")
      .d-flex
        .flex-grow-1
          AppToggleButtonGroup(
            :value="ableToModifyTime"
            label="Modify Time (manual time)"
            label-tooltip="Managers will be able to edit other team member's time"
            disabled-tooltip="Upgrade plan in order to modify time"
            :disabled="!allowModifyTime"
            :items="MODIFY_TIME_OPTIONS"
            :selected="ableToModifyTime"
            @input="updateModifyTimeField"
          )
        .checkbox.require-approval
          .badge.badge-new-mobile.mt-5.mb-5 New
          .d-flex
            AppOnOffToggle(
              v-model="ableToSendManualTimeForApproval"
              v-tooltip="disableManualTimeApproval"
              :disabled="disableManualTimeApproval"
              :data-original-title="manualTimeApprovalDisabledTooltip"
              data-placement="bottom"
            )
              .d-flex.align-items-center
                span Require approval
                i.hi.hi-info-filled.hi-12.a-tooltip.ml-5.mr-10(
                  v-tooltip
                  title="Requests are submitted for added manual time only"
                )
                span.badge.badge-new New
      //- Confirm Turbo dialog needs this to confirm change
      .hidden(@click="confirmModifyTimeDisabled")
        div(:id="`${membership.id}_modify_time_allowed_false`")

  #modify-time.form-group(v-if="!showModifyTimeWithManualTimeApproval")
    ValidationProvider(:model-value="ableToModifyTime")
      .checkbox
        AppOnOffToggle(
          v-model="ableToModifyTime"
          v-tooltip="!allowModifyTime"
          :disabled="!allowModifyTime || viewOnly"
          data-original-title="Upgrade plan in order to modify time"
          data-placement="bottom"
        )
          .d-flex.align-items-center
            span Able to modify time (manual time)
        i.hi.hi-info-filled.hi-12.a-tooltip.mx-5(
          v-tooltip
          :title="viewOnly ? 'Cannot enable time editing for a view-only member' : `Managers will be able to edit other team member's time`"
        )
  .d-flex
    h5 Project & app access
    .line-container
      .line
  #add-member.form-group(v-if="showAddMemberToProjects")
    ValidationProvider(:model-value="preselectUserRole")
      .d-flex
        .mb-15
          label.control-label
            .d-flex.align-items-center Project access
          .checkbox.align-items-start.flex-direction-column
            AppOnOffToggle(v-model="addMemberToProjects")
              span Add to all new projects
        .add-as(v-if="addMemberToProjects")
          label.control-label
            .d-flex.align-items-center Add as
          AppSelect.member-role(
            v-model="preselectUserRole"
            :options="preselectedUserRoleOptions"
            :select2-options="defaultSelect2Options"
          )
  #allowed-apps.form-group
    ValidationProvider(:model-value="allowedApps")
      AppToggleButtonGroup(
        v-model="allowedApps"
        label="Allowed apps"
        label-tooltip="This setting applies to all of the timer apps"
        :items="ALLOWED_APP_ITEMS"
        :selected="allowedApps"
      )
</template>

<script>
import { Turbo } from '@hotwired/turbo-rails';
import { mapState, mapGetters, mapActions } from 'vuex';
import { mapFormFields } from '~/utils/form_utils';
import IdleTimeoutSelect from '~/components/IdleTimeoutSelect.vue';
import { ALLOWED_APP_ITEMS, IDLE_TIMEOUT_ITEMS, KEEP_IDLE_TIME_ITEMS, MODIFY_TIME_OPTIONS } from '../../app_constants';

export default {
  name: 'MemberDialogTabSettings',
  components: { IdleTimeoutSelect },
  data() {
    return {
      ALLOWED_APP_ITEMS,
      MODIFY_TIME_OPTIONS,
      IDLE_TIMEOUT_ITEMS,
      KEEP_IDLE_TIME_ITEMS,
      addMemberToProjects: false,
      defaultSelect2Options: {
        allowClear: false,
        dropdownCssClass: 'preselect-role-dropdown',
        multiple: false,
        width: '100%',
      },
      jobTitleSelect2Options: {
        allowClear: false,
        width: '100%',
        placeholder: 'Select the Job Title',
      },
      initialIdleTimeout: null,
    };
  },
  computed: {
    ...mapState({
      membership: (state) => state.membership,
      initialMembershipRole: (state) => state.initial_membership_role,
    }),
    ...mapGetters([
      'allowModifyTime',
      'allowManualTimeApproval',
      'showModifyTimeWithManualTimeApproval',
      'customIdleTimeoutDisabled',
      'customIdleTimeoutSelected',
      'organizationOwner',
      'hasFeature',
      'hasKeepIdleTime',
      'preselectedUserRole',
      'preselectedUserRoleOptions',
      'allowModifyTimeSettingsPath',
      'organizationId',
      'membershipUser',
    ]),
    ...mapFormFields('membership', {
      ableToTrackTime: 'able_to_track_time',
      ableToModifyTime: 'able_to_modify_time',
      viewOnly: 'view_only',
      ableToSendManualTimeForApproval: 'able_to_send_manual_time_approval',
      preselectUserRole: 'preselect_user_role',
      allowedApps: 'client_platforms_allowed',
      idleTimeout: 'client_idle_timeout',
      keepIdleTime: 'client_keep_idle',
    }),
    showAddMemberToProjects() {
      return this.hasFeature('projects');
    },
    disableManualTimeApproval() {
      return !this.ableToModifyTime || !this.allowManualTimeApproval || this.organizationOwner;
    },
    manualTimeApprovalDisabledTooltip() {
      if (!this.allowManualTimeApproval) {
        return 'Upgrade your plan to access this premium feature.';
      }

      if (this.organizationOwner) {
        return 'Owners can submit manual time without approval required.';
      }

      return 'Modify time setting must be set to "Add & edit" to enable manual time approvals.';
    },
  },
  watch: {
    addMemberToProjects(val) {
      if (!val) {
        // Set preselected user role to none when addMemberToProjects is off
        this.updateField({ path: 'preselect_user_role', value: -1 });
      } else if (!this.preselectedUserRole) {
        this.updateField({ path: 'preselect_user_role', value: this.preselectedUserRoleOptions[0].value });
      }
    },
  },
  mounted() {
    if (this.preselectedUserRole) {
      this.addMemberToProjects = true;
    }

    this.initialIdleTimeout = this.idleTimeout;
  },
  methods: {
    ...mapActions(['updateField']),
    updateModifyTimeField(value) {
      if (value === false && this.hasFeature('manual_time_approval')) {
        return fetch(this.membership.confirm_modify_time_allowed_url).then(response => response.text()).then(Turbo.renderStreamMessage)
      }

      this.ableToModifyTime = value;
    },
    confirmModifyTimeDisabled() {
      this.ableToModifyTime = false;
      this.ableToSendManualTimeForApproval = false;
    },
  },
};
</script>
